import SparkMD5 from 'spark-md5';

import { http } from './http';

// Files picked in the resource submission form are kept as data URLs for previews.
// Before submitting, each one is uploaded in chunks (OS forwards them to the data
// warehouse, which writes them to storage) and the submission carries only its signed
// id: whole base64 files in the submission body stalled at the Heroku router.
export interface PendingResourceFile {
  base64: string;
  filename: string;
  content_type: string;
}

export interface UploadedResourceFile {
  signed_id: string;
  filename: string;
  content_type: string;
}

interface UploadSlot {
  signed_id: string;
  chunk_size: number;
}

interface UploadFailure {
  message?: string;
}

export class ResourceUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ResourceUploadError';
    Object.setPrototypeOf(this, ResourceUploadError.prototype);
  }
}

const CHUNK_ATTEMPTS = 3;

// Reuses an upload when the same picked file is submitted again, e.g. after a validation error.
const uploads = new WeakMap<PendingResourceFile, Promise<UploadedResourceFile>>();

const isPendingFile = (value: unknown): value is PendingResourceFile =>
  !!value &&
  typeof value === 'object' &&
  typeof (value as PendingResourceFile).base64 === 'string' &&
  (value as PendingResourceFile).base64.startsWith('data:');

const decode = (dataUrl: string) => {
  const binary = atob(dataUrl.slice(dataUrl.indexOf(',') + 1));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

const md5 = (bytes: Uint8Array) => btoa(SparkMD5.ArrayBuffer.hash(bytes.buffer, true));

const failureMessage = (data: unknown) =>
  data && typeof data === 'object' && 'message' in data
    ? (data as UploadFailure).message
    : undefined;

const unfinished = (file: PendingResourceFile) =>
  new ResourceUploadError(
    `${file.filename} didn't finish uploading. Check your connection and try again.`
  );

// Retries a chunk when the connection or the server failed, not when the chunk was rejected.
const putChunk = async (
  file: PendingResourceFile,
  signedId: string,
  index: number,
  chunk: Uint8Array
) => {
  for (let attempt = 1; ; attempt++) {
    const response = await http.put<unknown, UploadFailure>(
      'resources/direct_upload_chunk',
      new Blob([chunk], { type: 'application/octet-stream' }),
      {
        params: { signed_id: signedId, index, checksum: md5(chunk) },
        headers: { 'Content-Type': 'application/octet-stream' },
      }
    );
    if (response.ok) return;

    const rejected = response.status && response.status < 500;
    if (rejected)
      throw new ResourceUploadError(failureMessage(response.data) || unfinished(file).message);
    if (attempt === CHUNK_ATTEMPTS) throw unfinished(file);
  }
};

const upload = async (file: PendingResourceFile): Promise<UploadedResourceFile> => {
  const bytes = decode(file.base64);
  const contentType = file.content_type || 'application/octet-stream';
  const slot = await http.post<UploadSlot, UploadFailure>('resources/direct_upload', {
    blob: {
      filename: file.filename,
      content_type: contentType,
      byte_size: bytes.byteLength,
      checksum: md5(bytes),
    },
  });
  if (!slot.ok || !slot.data || !('signed_id' in slot.data)) {
    throw new ResourceUploadError(
      failureMessage(slot.data) || `We couldn't upload ${file.filename}. Please try again.`
    );
  }

  const { signed_id, chunk_size } = slot.data;
  for (let index = 0; index * chunk_size < bytes.byteLength; index++) {
    const start = index * chunk_size;
    await putChunk(file, signed_id, index, bytes.slice(start, start + chunk_size));
  }

  const completed = await http.post<unknown, UploadFailure>('resources/direct_upload_complete', {
    signed_id,
  });
  if (!completed.ok) {
    throw new ResourceUploadError(failureMessage(completed.data) || unfinished(file).message);
  }

  return { signed_id, filename: file.filename, content_type: contentType };
};

export const uploadResourceFile = (file: PendingResourceFile) => {
  if (!uploads.has(file)) {
    uploads.set(
      file,
      upload(file).catch((error) => {
        uploads.delete(file);
        throw error;
      })
    );
  }
  return uploads.get(file);
};

const uploadIfPending = (value: unknown) =>
  isPendingFile(value) ? uploadResourceFile(value) : Promise.resolve(value);

// Replaces every picked file in a submission payload with its uploaded reference.
export const withUploadedResourceFiles = async <T extends Record<string, any>>(
  payload: T
): Promise<T> => {
  const [file, image, additionalImages] = await Promise.all([
    uploadIfPending(payload.file),
    uploadIfPending(payload.image),
    Array.isArray(payload.additional_images)
      ? Promise.all(payload.additional_images.map(uploadIfPending))
      : Promise.resolve(payload.additional_images),
  ]);
  return { ...payload, file, image, additional_images: additionalImages };
};
